import type { ObjectDirective } from 'vue'

interface ReasonState { node: HTMLElement; ownsDisabled: boolean; ownsNode: boolean; reason: string }
const states = new WeakMap<HTMLElement, ReasonState>()
const reasonGroups = new WeakMap<HTMLElement, Map<string, HTMLElement>>()
let nextId = 0
function passive(element: HTMLElement, state?: ReasonState) {
  return element.matches(':disabled') || (!state?.ownsDisabled && element.getAttribute('aria-disabled') === 'true')
}
function remove(element: HTMLElement) {
  const state = states.get(element)
  if (!state) return
  const ids = (element.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(id => id && id !== state.node.id)
  if (ids.length) element.setAttribute('aria-describedby', ids.join(' '))
  else element.removeAttribute('aria-describedby')
  if (state.ownsDisabled) element.removeAttribute('aria-disabled')
  if (state.ownsNode && !document.querySelector(`[aria-describedby~='${state.node.id}']`)) state.node.remove()
  states.delete(element)
}
export function updatePassiveReason(element: HTMLElement, reason?: string) {
  let previous = states.get(element)
  if (!passive(element, previous) || !reason?.trim()) { remove(element); return }
  const hint = element.querySelector<HTMLElement>('small, .item-hint')
  const fieldAttribute = element.hasAttribute('data-pending-module') ? 'data-pending-module' : 'data-pending-field'
  const field = element.getAttribute(fieldAttribute)
  const region = element.closest('section, header, .saas-panel, .customer-filters') ?? element.parentElement
  const note = field ? [...region?.querySelectorAll<HTMLElement>('.saas-pending, .crm-pending') ?? []]
    .find(node => node !== element && node.getAttribute(fieldAttribute) === field && node.textContent?.trim()) : undefined
  const parent = element.parentElement
  const horizontal = (node: HTMLElement | null) => node && /^(inline-)?flex$/.test(getComputedStyle(node).display) && (!getComputedStyle(node).flexDirection || getComputedStyle(node).flexDirection.startsWith('row'))
  const menuItem = element.matches('.fw-chip-menu > button')
  let anchor = parent?.matches('.fw-list-pager, .fw-chip-row') || horizontal(parent) ? parent! : element
  while (horizontal(anchor.parentElement)) anchor = anchor.parentElement!
  // An opted-in bordered list keeps its pager as the final row. Its visible
  // descriptions follow the card and retain their aria-describedby links.
  if (anchor.matches('.fw-list-pager')) anchor = anchor.closest<HTMLElement>('[data-fw-passive-reason-anchor]') ?? anchor
  const shared = menuItem ? undefined : reasonGroups.get(anchor)?.get(reason)
  if (previous?.ownsNode && previous.reason !== reason) {
    const usedByOthers = [...document.querySelectorAll(`[aria-describedby~='${previous.node.id}']`)].some(node => node !== element)
    if (usedByOthers || shared?.isConnected && shared !== previous.node) { remove(element); previous = undefined }
    else if (!menuItem) {
      const group = reasonGroups.get(anchor)
      if (group?.get(previous.reason) === previous.node) group.delete(previous.reason)
      group?.set(reason, previous.node)
    }
  }
  const described = (element.getAttribute('aria-describedby') ?? '').split(/\s+/)
    .map(id => element.ownerDocument.getElementById(id) ?? [...element.parentElement?.querySelectorAll<HTMLElement>('[id]') ?? []].find(node => node.id === id))
    .find(node => node?.textContent?.trim() === reason.trim())
  const existing = described ?? (shared?.isConnected ? shared : undefined) ?? note ?? (hint?.textContent?.trim() === reason.trim() ? hint : null)
  const state = previous ?? { node: existing ?? document.createElement('span'), ownsDisabled: !element.hasAttribute('aria-disabled'), ownsNode: !existing || existing === shared, reason }
  if (!previous) {
    state.node.id ||= `fw-passive-reason-${++nextId}`
    if (state.ownsNode) {
      state.node.className = 'fw-passive-reason'
      if (menuItem) element.append(state.node)
      else {
        // Keep notes outside measured rows; several controls can share one visible reason.
        if (!reasonGroups.has(anchor)) reasonGroups.set(anchor, new Map())
        reasonGroups.get(anchor)!.set(reason, state.node)
        if (!state.node.isConnected) anchor.after(state.node)
      }
    }
    states.set(element, state)
  }
  state.reason = reason
  if (state.ownsNode && state.node.textContent !== reason) state.node.textContent = reason
  if (element.getAttribute('aria-disabled') !== 'true') element.setAttribute('aria-disabled', 'true')
  const ids = new Set((element.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean))
  ids.add(state.node.id)
  const description = [...ids].join(' ')
  if (element.getAttribute('aria-describedby') !== description) element.setAttribute('aria-describedby', description)
}

/** Shared controls keep their Work implementation. Cosmos observes only its mounted
 * document, including ChipRow's teleported menu, and binds existing localized hints. */
export function observePassiveReasons(root: HTMLElement, resolve?: (element: HTMLElement) => string | undefined) {
  const managed = new Set<HTMLElement>()
  let frame: number | undefined
  let disposed = false
  const observer = new MutationObserver(() => {
    if (frame !== undefined || disposed) return
    frame = requestAnimationFrame(() => { frame = undefined; if (!disposed) refresh() })
  })
  function refresh() {
    observer.disconnect()
    const controls = new Set([...managed, ...root.querySelectorAll<HTMLElement>(':disabled, [aria-disabled="true"]')])
    for (const element of controls) {
      if (!element.isConnected) { remove(element); managed.delete(element); continue }
      if (!states.has(element) && element.hasAttribute('aria-describedby')) continue
      const reason = resolve?.(element) ?? element.getAttribute('aria-description') ?? element.getAttribute('title')
        ?? element.querySelector('small, .item-hint')?.textContent ?? undefined
      if (reason?.trim()) { updatePassiveReason(element, reason); managed.add(element) }
    }
    observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'title', 'aria-description', 'aria-label'] })
  }
  refresh()
  return () => {
    disposed = true
    observer.disconnect()
    if (frame !== undefined) cancelAnimationFrame(frame)
    frame = undefined
    for (const element of managed) remove(element)
    managed.clear()
  }
}
function preventActivation(event: Event) {
  if (event.currentTarget instanceof HTMLElement && passive(event.currentTarget)) {
    if (event instanceof KeyboardEvent && !['Enter', ' '].includes(event.key)) return
    event.preventDefault(); event.stopImmediatePropagation()
  }
}
/** The surface supplies the translated reason; no business rules or text live in shared UI. */
export const vPassiveReason: ObjectDirective<HTMLElement, string | undefined> = {
  mounted(element, { value }) {
    updatePassiveReason(element, value ?? element.getAttribute('title') ?? undefined)
    element.addEventListener('click', preventActivation, true)
    element.addEventListener('keydown', preventActivation, true)
  },
  updated(element, { value }) { updatePassiveReason(element, value ?? element.getAttribute('title') ?? undefined) },
  beforeUnmount(element) {
    remove(element)
    element.removeEventListener('click', preventActivation, true)
    element.removeEventListener('keydown', preventActivation, true)
  },
}
